/* ---------------------------------------------------------------------------
   Frame-accurate <video> timing primitives.

   Everything in Badly Dubbed is "seek to start, play, stop exactly at end".
   `timeupdate` fires roughly every 250ms, which is far too coarse to stop a
   5-second sub-chunk on the right frame, so we drive off
   requestVideoFrameCallback where it exists and fall back to rAF elsewhere.

   No DOM ownership: callers hand us an element, we never create or attach one.
   --------------------------------------------------------------------------- */

/** Tolerance for "close enough" time comparisons. ~1 frame at 50fps. */
export const FRAME_EPSILON = 0.02;

const DEFAULT_SEEK_TIMEOUT_MS = 5_000;
const DEFAULT_METADATA_TIMEOUT_MS = 20_000;
/** Backstop poll in case neither rVFC nor rAF fires (backgrounded tab). */
const WATCHDOG_MS = 40;

export class AbortedError extends Error {
  constructor(message = "Operation aborted") {
    super(message);
    this.name = "AbortedError";
  }
}

export class MediaPlaybackError extends Error {
  readonly code: "blocked" | "decode" | "timeout";
  constructor(message: string, code: "blocked" | "decode" | "timeout", cause?: unknown) {
    super(message);
    this.name = "MediaPlaybackError";
    this.code = code;
    if (cause !== undefined) this.cause = cause;
  }
}

// --- requestVideoFrameCallback ---------------------------------------------

/** Only the field we use. Kept local so we never clash with lib.dom. */
interface FrameMetadata {
  mediaTime: number;
}
type FrameCallback = (now: number, metadata: FrameMetadata) => void;

interface FrameCapableVideo {
  requestVideoFrameCallback?: (cb: FrameCallback) => number;
  cancelVideoFrameCallback?: (handle: number) => void;
}

function frameApi(video: HTMLVideoElement): FrameCapableVideo {
  return video as unknown as FrameCapableVideo;
}

export function supportsVideoFrameCallback(video?: HTMLVideoElement): boolean {
  if (typeof document === "undefined") return false;
  const el = video ?? document.createElement("video");
  return typeof frameApi(el).requestVideoFrameCallback === "function";
}

export type CancelFrameLoop = () => void;

/**
 * Call `cb` once per presented video frame (rVFC) or once per animation frame
 * (fallback). Returns a cancel function that is safe to call more than once.
 *
 * Note: rVFC only fires while the element is actually presenting frames. Use
 * `onAnimationFrame` for loops that must keep running while paused.
 */
export function onEachFrame(
  video: HTMLVideoElement,
  cb: (mediaTime: number) => void,
): CancelFrameLoop {
  let cancelled = false;
  const api = frameApi(video);

  if (
    typeof api.requestVideoFrameCallback === "function" &&
    typeof api.cancelVideoFrameCallback === "function"
  ) {
    const request = api.requestVideoFrameCallback.bind(video);
    const cancel = api.cancelVideoFrameCallback.bind(video);
    let handle = 0;
    const step: FrameCallback = (_now, metadata) => {
      if (cancelled) return;
      const t = typeof metadata?.mediaTime === "number" ? metadata.mediaTime : video.currentTime;
      cb(t);
      if (!cancelled) handle = request(step);
    };
    handle = request(step);
    return () => {
      if (cancelled) return;
      cancelled = true;
      cancel(handle);
    };
  }

  let raf = 0;
  const tick = () => {
    if (cancelled) return;
    cb(video.currentTime);
    if (!cancelled) raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);
  return () => {
    if (cancelled) return;
    cancelled = true;
    cancelAnimationFrame(raf);
  };
}

/** rAF loop that runs regardless of playback state. Used for meters/countdowns. */
export function onAnimationFrame(cb: (now: number) => void): CancelFrameLoop {
  let cancelled = false;
  let raf = 0;
  const tick = (now: number) => {
    if (cancelled) return;
    cb(now);
    if (!cancelled) raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);
  return () => {
    if (cancelled) return;
    cancelled = true;
    cancelAnimationFrame(raf);
  };
}

// --- helpers ----------------------------------------------------------------

export function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new AbortedError();
}

/** Clamp a seek target inside the media's real range. */
function safeTime(video: HTMLVideoElement, time: number): number {
  const duration = video.duration;
  const upper = Number.isFinite(duration) && duration > 0 ? duration - FRAME_EPSILON : Infinity;
  return clamp(time, 0, upper);
}

/** Resolve once the element knows its duration/dimensions. */
export function waitForMetadata(
  video: HTMLVideoElement,
  timeoutMs: number = DEFAULT_METADATA_TIMEOUT_MS,
): Promise<void> {
  if (video.readyState >= HTMLMediaElement.HAVE_METADATA) return Promise.resolve();

  return new Promise<void>((resolve, reject) => {
    const cleanup = () => {
      video.removeEventListener("loadedmetadata", onLoaded);
      video.removeEventListener("error", onError);
      window.clearTimeout(timer);
    };
    const onLoaded = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new MediaPlaybackError("The browser could not decode this video.", "decode", video.error));
    };
    const timer = window.setTimeout(() => {
      cleanup();
      reject(new MediaPlaybackError("Timed out reading video metadata.", "timeout"));
    }, timeoutMs);

    video.addEventListener("loadedmetadata", onLoaded);
    video.addEventListener("error", onError);
  });
}

/**
 * Seek and wait for the frame to actually land.
 *
 * Resolves (rather than rejecting) on timeout: a slow seek should not blow up a
 * round. The caller's frame loop still enforces the real boundaries.
 */
export function seekTo(
  video: HTMLVideoElement,
  time: number,
  options: { signal?: AbortSignal; timeoutMs?: number } = {},
): Promise<void> {
  const { signal, timeoutMs = DEFAULT_SEEK_TIMEOUT_MS } = options;
  if (signal?.aborted) return Promise.reject(new AbortedError());

  const target = safeTime(video, time);
  if (Math.abs(video.currentTime - target) < FRAME_EPSILON && !video.seeking) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    const cleanup = () => {
      video.removeEventListener("seeked", onSeeked);
      video.removeEventListener("error", onError);
      signal?.removeEventListener("abort", onAbort);
      window.clearTimeout(timer);
    };
    const onSeeked = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new MediaPlaybackError("Playback failed while seeking.", "decode", video.error));
    };
    const onAbort = () => {
      cleanup();
      reject(new AbortedError());
    };
    const timer = window.setTimeout(() => {
      cleanup();
      resolve();
    }, timeoutMs);

    video.addEventListener("seeked", onSeeked);
    video.addEventListener("error", onError);
    signal?.addEventListener("abort", onAbort);

    try {
      video.currentTime = target;
    } catch (err) {
      cleanup();
      reject(new MediaPlaybackError("Could not seek this video.", "decode", err));
    }
  });
}

/**
 * Unlock a <video> element for later programmatic playback. iOS ONLY.
 *
 * MUST be called SYNCHRONOUSLY inside a real pointer/click handler, before the
 * first `await` — iOS Safari grants an element permission to play from the
 * gesture task only, and any `await` (a `seeked` event, `getUserMedia`, an
 * AudioContext resume) has already moved us off it.
 *
 * Muted playback is exempt from the gesture requirement, so this muted
 * play/pause is always permitted; what it buys is the element's *persistent*
 * activation, which is what later unmuted `play()` calls rely on. Without it
 * the first unmuted refresher pass rejects with NotAllowedError and the player
 * never hears the clip they are supposed to dub.
 *
 * Deliberately not async: awaiting it would defeat its entire purpose.
 */
export function primeVideoForPlayback(video: HTMLVideoElement): void {
  video.playsInline = true;
  video.setAttribute("playsinline", "");
  const wasMuted = video.muted;
  video.muted = true;

  try {
    const started = video.play();
    if (started && typeof started.then === "function") {
      started.then(
        () => {
          video.pause();
          video.muted = wasMuted;
        },
        () => {
          video.muted = wasMuted;
        },
      );
    } else {
      video.pause();
      video.muted = wasMuted;
    }
  } catch {
    video.muted = wasMuted;
  }
}

/** Start playback, translating autoplay rejections into a typed error. */
export async function startPlayback(video: HTMLVideoElement): Promise<void> {
  try {
    const result = video.play();
    if (result && typeof result.then === "function") await result;
  } catch (err) {
    const name = err instanceof Error ? err.name : "";
    if (name === "NotAllowedError") {
      throw new MediaPlaybackError(
        "The browser blocked playback. Tap the button again to start.",
        "blocked",
        err,
      );
    }
    throw new MediaPlaybackError("Playback could not start.", "decode", err);
  }
}

export type PlayUntilReason = "reached" | "ended" | "aborted";

export interface PlayUntilOptions {
  signal?: AbortSignal;
  /** Called on every presented frame with the current media time. */
  onFrame?: (mediaTime: number) => void;
  /** Pause the element when the boundary is hit. Default true. */
  pauseAtEnd?: boolean;
}

/**
 * Play from the current position and stop precisely at `end`.
 *
 * Uses requestVideoFrameCallback when available (frame accurate), rAF
 * otherwise, plus a 40ms watchdog so a throttled tab still stops on time.
 */
export async function playUntil(
  video: HTMLVideoElement,
  end: number,
  options: PlayUntilOptions = {},
): Promise<PlayUntilReason> {
  const { signal, onFrame, pauseAtEnd = true } = options;
  throwIfAborted(signal);

  if (video.currentTime >= end - FRAME_EPSILON) {
    if (pauseAtEnd) video.pause();
    return "reached";
  }

  await startPlayback(video);
  throwIfAborted(signal);

  return new Promise<PlayUntilReason>((resolve, reject) => {
    let settled = false;
    let cancelFrames: CancelFrameLoop = () => {};

    const finish = (reason: PlayUntilReason) => {
      if (settled) return;
      settled = true;
      cleanup();
      if (pauseAtEnd) video.pause();
      resolve(reason);
    };
    const fail = (err: unknown) => {
      if (settled) return;
      settled = true;
      cleanup();
      video.pause();
      reject(err);
    };
    const cleanup = () => {
      cancelFrames();
      window.clearInterval(watchdog);
      video.removeEventListener("ended", onEnded);
      video.removeEventListener("error", onError);
      signal?.removeEventListener("abort", onAbort);
    };

    const check = (mediaTime: number) => {
      if (settled) return;
      onFrame?.(mediaTime);
      if (mediaTime >= end - FRAME_EPSILON) finish("reached");
    };

    const onEnded = () => finish("ended");
    const onError = () =>
      fail(new MediaPlaybackError("Playback failed mid-clip.", "decode", video.error));
    const onAbort = () => finish("aborted");

    const watchdog = window.setInterval(() => check(video.currentTime), WATCHDOG_MS);
    video.addEventListener("ended", onEnded);
    video.addEventListener("error", onError);
    signal?.addEventListener("abort", onAbort);

    cancelFrames = onEachFrame(video, check);
    check(video.currentTime);
  });
}
